import {
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Tabs,
} from "antd";
import { Rule } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import { UploadFile } from "antd/lib/upload/interface";
import { productCategoryApi } from "api/product-category.api";
import { productApi } from "api/product.api";
import { AppButton } from "components/antd-ui";
import { MultiImageUpload } from "components/Upload/MultiImageUpload";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import React, { useEffect, useMemo, useState } from "react";
import { ModalStatus } from "types/modal";
import { Product } from "types/product";
import { ProductCategory } from "types/product-category";
import InputMask from "react-input-mask";
import { InputNumber } from "components/Input/InputNumber";
import { ProductPropertyTab } from "./ProductPropertyTab";

const rules: Rule[] = [{ required: true }];

const { TabPane } = Tabs;

const { Option } = Select;

interface ProductForm extends Product {
  productCategoryId: number;
}

export const ProductModal = ({
  visible,
  status,
  product,
  onClose,
  onSubmitOk,
  shopId,
}: {
  visible: boolean;
  status: ModalStatus;
  shopId: number;
  product: Partial<Product>;
  onClose: () => void;
  onSubmitOk: () => void;
}) => {
  const [form] = Form.useForm<ProductForm>();
  const [loading, setLoading] = useState(false);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    []
  );
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (status == "create" && visible) {
      form.resetFields();
    }

    if (visible) {
      fetchProductCategory();
    }
  }, [visible, status]);

  const fetchProductCategory = async () => {
    const res = await productCategoryApi.findAll({
      page: 1,
      limit: 0,
      shopId,
    });
    setProductCategories(res.data.productCategories);
  };

  useEffect(() => {
    form.setFieldsValue({
      ...product,
      productCategoryId: product.productCategory?.id,
    });
    return () => {};
  }, [product]);

  const createData = async () => {
    const valid = await form.validateFields();
    const data = {
      product: form.getFieldsValue(),
      shopId,
      productCategoryId: form.getFieldValue("productCategoryId"),
      images,
    };

    setLoading(true);
    try {
      const res = await productApi.create(data);
      message.success("Tạo sản phẩm thành công");
      onClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    const valid = await form.validateFields();
    const data = {
      product: form.getFieldsValue(),
      shopId,
      productCategoryId: form.getFieldValue("productCategoryId"),
      images,
    };
    setLoading(true);
    try {
      const res = await productApi.update(product?.id || 0, data);
      message.success("Cập nhật sản phẩm thành công");
      onClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const imageList = useMemo<UploadFile<any>[]>(() => {
    if (product.images) {
      return product.images.map((e) => ({
        name: e.id + "",
        uid: e.id + "",
        url: e.url,
      }));
    }
    setImages([]);
    return [];
  }, [product.images]);

  const handleSubmit = async () => {
    if (status === "create") {
      await createData();
      return;
    }

    await updateData();
  };

  return (
    <Modal
      style={{ top: 15 }}
      onCancel={onClose}
      open={visible}
      title={
        <h1 className="text-xl font-bold text-center">
          {status === "create" ? "Tạo mới sản phẩm" : "Cập nhật sản phẩm"}
        </h1>
      }
      footer={null}
      width={1200}
      className="app-form-modal"
    >
      <div className="app-form-modal__body">
        <section className="app-form-modal__section">
          <Tabs defaultActiveKey="1" className="app-form-modal__tabs">
            <TabPane tab="Thông tin" key="1">
              <Form layout="vertical" form={form}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label="Tên sản phẩm" name="name" rules={rules}>
                          <Input placeholder="" />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Giá gốc" name="originPrice" rules={rules}>
                          <InputNumber />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Giá khuyến mãi" name="finalPrice">
                          <InputNumber />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Tình trạng kho" name="isOutOfStock">
                          <Radio.Group>
                            <Radio value={true}>Hết hàng</Radio>
                            <Radio value={false}>Còn hàng</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label="Danh mục"
                          name="productCategoryId"
                          rules={rules}
                        >
                          <Select style={{ width: "100%" }}>
                            {productCategories.map((e) => (
                              <Option key={e.id} value={e.id}>
                                {e.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item label="Mô tả" name="description" rules={rules}>
                          <TextArea placeholder="" rows={5} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>

                  <Col span={12}>
                    <Form.Item shouldUpdate={true}>
                      {() => {
                        return (
                          <Form.Item label="Hình ảnh" name="image">
                            <SingleImageUpload
                              onUploadOk={(path: string) => {
                                form.setFieldsValue({
                                  image: path,
                                });
                              }}
                              imageUrl={form.getFieldValue("image")}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <Form.Item shouldUpdate={true}>
                      {() => {
                        return (
                          <Form.Item label="Ảnh khác" name="thumbnails">
                            <MultiImageUpload
                              onUploadOk={(fileList) => {
                                setImages(fileList);
                              }}
                              fileListProp={imageList}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </TabPane>

            {status === "create" && (
              <TabPane tab="Thuộc tính" key="2">
                <ProductPropertyTab productId={product.id} />
              </TabPane>
            )}
          </Tabs>
        </section>
      </div>
      <div className="ant-modal-footer">
        <div className="flex gap-2 justify-end">
          <AppButton onClick={onClose}>Hủy</AppButton>
          <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
            {status === "create" ? "Xác nhận" : "Cập nhật"}
          </AppButton>
        </div>
      </div>
    </Modal>
  );
};
